import React from "react";
import styled from "styled-components";
import GroupBasketItemImage from "./GroupBasketItemImage";
import AddIcon from "@mui/icons-material/Add";
import {
  MEMBER_CONTROL,
  formatBasketMoney,
  memberControls,
  shareOfMember,
  shareStatusLabel,
  shareStatusTone,
  unpricedText,
} from "../../utils/groupBasketView";
import {
  Card,
  CardTitle,
  GREEN_SOFT,
  INK,
  LINE,
  MUTED,
  Muted,
  Pill,
  PrimaryButton,
  TEXT,
  TextButton,
} from "./groupBasketStyles";

const itemCount = (lines) =>
  (lines || []).filter((line) => !line.dropped).reduce((sum, line) => sum + (Number(line.qty) || 0), 0);

const optionNames = (line) =>
  (line.options || [])
    .map((option) => option?.name)
    .filter(Boolean)
    .join(", ");

/*
 * Everyone in the basket and what each chose, as the server prices it. A line
 * the order cannot carry (sold out, not sold here, dropped) is struck through
 * with the reason. The caller's own lines can be edited while the server
 * allows it (`canEditLines`).
 */
const GroupBasketMembers = ({
  basket,
  canEditLines,
  menuReady,
  busy,
  onAddItems,
  onEditLine,
  onRemoveLine,
  onMemberControl,
}) => {
  const members = basket?.members || [];
  const currency = basket?.currency;
  const me = members.find((member) => member.isYou);

  return (
    <Card aria-label="Who is in the basket">
      <HeadRow>
        <CardTitle>In this basket</CardTitle>
        <Pill $tone="todo">
          {members.length} of {basket?.maxMembers || members.length}
        </Pill>
      </HeadRow>

      <List>
        {members.map((member) => {
          const share = shareOfMember(basket, member.memberId);
          const controls = memberControls(basket, member);
          const lines = member.lines || [];
          const count = itemCount(lines);
          const mine = member.isYou;
          return (
            <Member key={member.memberId} $mine={mine}>
              <MemberHead>
                <Avatar aria-hidden="true">{(member.label || "?").slice(0, 1).toUpperCase()}</Avatar>
                <MemberName>
                  <strong>{member.label}</strong>
                  <Tags>
                    {mine && <Pill $tone="done">You</Pill>}
                    {member.isHost && <Pill $tone="todo">Host</Pill>}
                    {share && <Pill $tone={shareStatusTone(share.status)}>{shareStatusLabel(share.status)}</Pill>}
                  </Tags>
                  <small>{count === 0 ? "No items yet" : `${count} ${count === 1 ? "item" : "items"}`}</small>
                </MemberName>
                <Amount>{formatBasketMoney(member.itemsSubtotal, currency)}</Amount>
              </MemberHead>

              {lines.length > 0 && (
                <Lines>
                  {lines.map((line) => {
                    const struck = Boolean(line.unpricedReason) || line.dropped === true;
                    const options = optionNames(line);
                    return (
                      <Line key={line.lineId}>
                        <GroupBasketItemImage imageUrl={line.imageUrl} />
                        <LineMain $struck={struck}>
                          <span className="what">
                            <b>{line.qty} x</b> {line.name}
                          </span>
                          {options ? <span className="options">{options}</span> : null}
                          {line.dropped ? (
                            <span className="why">Dropped by the host. Not in the order.</span>
                          ) : line.unpricedReason ? (
                            <span className="why">{unpricedText(line.unpricedReason)}</span>
                          ) : null}
                        </LineMain>
                        <LineSide>
                          <span className="total">{formatBasketMoney(line.lineTotal, currency)}</span>
                          {mine && canEditLines && (
                            <span className="actions">
                              {menuReady && !struck && (
                                <TextButton type="button" disabled={busy} onClick={() => onEditLine(line)}>
                                  Edit
                                </TextButton>
                              )}
                              <TextButton
                                type="button"
                                $danger
                                disabled={busy}
                                onClick={() => onRemoveLine(line)}
                                aria-label={`Remove ${line.name}`}
                              >
                                Remove
                              </TextButton>
                            </span>
                          )}
                        </LineSide>
                      </Line>
                    );
                  })}
                </Lines>
              )}

              {/* Dropping unpaid items sits beside "Pay what is left", on the page. */}
              {controls.includes(MEMBER_CONTROL.REMOVE) && (
                <MemberFoot>
                  <TextButton type="button" $danger disabled={busy} onClick={() => onMemberControl(member, MEMBER_CONTROL.REMOVE)}>
                    Remove {member.label}
                  </TextButton>
                </MemberFoot>
              )}

              {mine && canEditLines && (
                <MineFoot>
                  {count === 0 && <Muted>You have not added anything yet.</Muted>}
                  <PrimaryButton type="button" onClick={onAddItems} disabled={busy}>
                    <AddIcon aria-hidden="true" />
                    {count === 0 ? "Add my items" : "Add more items"}
                  </PrimaryButton>
                </MineFoot>
              )}
            </Member>
          );
        })}
      </List>
      {!me && <Muted>You are not in this basket.</Muted>}
    </Card>
  );
};

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Member = styled.li`
  padding: 14px 0;

  & + & {
    border-top: 1px solid ${LINE};
  }
`;

const MemberHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Avatar = styled.span`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: ${GREEN_SOFT};
  color: ${INK};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
`;

const MemberName = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-size: 16px;
    font-weight: 800;
    color: ${TEXT};
    overflow-wrap: anywhere;
  }

  small {
    font-size: 13px;
    color: ${MUTED};
  }
`;

const Tags = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Amount = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${TEXT};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Lines = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 380px) {
    margin-left: 0;
  }
`;

const Line = styled.li`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const LineMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
  line-height: 1.4;
  color: ${TEXT};

  .what,
  .options {
    text-decoration: ${(p) => (p.$struck ? "line-through" : "none")};
    opacity: ${(p) => (p.$struck ? 0.6 : 1)};
  }

  .options {
    font-size: 13px;
    color: ${MUTED};
  }

  .why {
    font-size: 13px;
    font-weight: 700;
    color: #8a5a00;
  }
`;

const LineSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;

  .total {
    font-size: 14px;
    font-weight: 700;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .actions {
    display: flex;
    margin-right: -8px;
  }
`;

const MemberFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

const MineFoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 12px 0 0 56px;

  > button {
    flex: none;
  }

  @media (max-width: 380px) {
    margin-left: 0;
  }
`;

export default GroupBasketMembers;
